import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { appInsights } from '../../../../telemetry/appInsights';
import { useTrackEvent } from '@microsoft/applicationinsights-react-js';
import { formatInventoryReviewStatus, formatSignOnTimestamp } from '../helpers/utils';
import '../styles/aurora.css';

function formatCellValue(value) {
  if (value == null || value === '') {
    return '—';
  }
  return String(value);
}

function sortGames(products) {
  return products.sort((a, b) => a.name.localeCompare(b.name));
}

function sortPacks(packs) {
  return packs.sort((a, b) => b.packId - a.packId);
}

// Game list text: product id, then the game name. 
function gameLabel(game) {
  if (!game) {
    return '';
  }
  const gameId = game.productId ?? '';
  const gameName = game.name ?? '';
  if (gameId === '') {
    return String(gameName);
  }
  if (!gameName) {
    return String(gameId);
  }
  return `${gameId} | ${gameName}`;
}

// Keep games whose id or name contains the typed text
function filterGames(options, query, selectedLabel) {
  const searchText = query.trim().toLowerCase();
  if (!searchText || searchText === selectedLabel.trim().toLowerCase()) {
    return options;
  }
  return options.filter(game => gameLabel(game).toLowerCase().includes(searchText));
}

export default function InventoryReviewModal({
  open,
  onClose,
  site,
  user,
  translations = {},
}) {
  const trackInventoryReview = useTrackEvent(appInsights, 'InventoryReview');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [productId, setProductId] = useState('');
  const [gameQuery, setGameQuery] = useState('');
  const [unitId, setUnitId] = useState('');
  const [searching, setSearching] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [packs, setPacks] = useState(null);
  const [searchError, setSearchError] = useState('');

  useEffect(() => {
    if (!open) {
      setProductId('');
      setGameQuery('');
      setUnitId('');
      setOrderNumber('');
      setPacks(null);
      setSearchError('');
      setProductsError('');
      return;
    }

    let cancelled = false;

    const loadProducts = async () => {
      if (!site) {
        setProductsError('Site is required to load games.');
        setProducts([]);
        return;
      }

      setProductsLoading(true);
      setProductsError('');
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_ENDPOINT_HOST}/inventory/products`,
          { params: { site } },
        );
        if (!cancelled) {
          const gameList = Array.isArray(response.data) ? response.data : [];
          setProducts(sortGames(gameList));
        }
      } catch (error) {
        if (!cancelled) {
          const errorMessage = error.response?.data?.error || 'Failed to fetch product names';
          setProducts([]);
          setProductsError(errorMessage);
        }
      } finally {
        if (!cancelled) {
          setProductsLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      cancelled = true;
    };
  }, [open, site]);

  const handleSubmit = async () => {
    const selected = products.find(
      product => String(product.productId) === String(productId),
    );
    setSearching(true);
    setSearchError('');
    setOrderNumber('');
    setPacks(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT_HOST}/inventory/orderInfo`,
        { params: { site, productId, unitId: unitId.trim() } },
      );
      const nextPacks = sortPacks(response.data?.packs ?? []);
      const nextOrderNumber = response.data?.orderNumber ?? '';
      setOrderNumber(nextOrderNumber);
      setPacks(nextPacks);
      try {
        trackInventoryReview({
          user,
          game: { productId, name: selected?.name ?? '' },
          unitId: unitId.trim(),
          orderNumber: nextOrderNumber,
          packs: nextPacks,
        });
      } catch (telemetryError) {
        console.warn('Telemetry InventoryReview failed:', telemetryError);
      }
    } catch (error) {
      const message =
        error.response?.data?.error || 'Failed to retrieve inventory order info';
      setSearchError(message);
      setPacks([]);
      try {
        trackInventoryReview({
          user,
          game: { productId, name: selected?.name ?? '' },
          unitId: unitId.trim(),
          error: message,
        });
      } catch (telemetryError) {
        console.warn('Telemetry InventoryReview failed:', telemetryError);
      }
    } finally {
      setSearching(false);
    }
  };

  const canSubmit = Boolean(productId) && Boolean(unitId.trim()) && !searching;
  const selectedGame = products.find(product => String(product.productId) === String(productId)) ?? null;

  const handleGameChange = (event, game) => {
    setProductId(game?.productId ?? '');
    setOrderNumber('');
    setPacks(null);
    setSearchError('');
  };

  const handleFormSubmit = event => {
    event.preventDefault();
    if (canSubmit) {
      handleSubmit();
    }
  };

  const handleClear = () => {
    setProductId('');
    setUnitId('');
    setOrderNumber('');
    setPacks(null);
    setSearchError('');
  };

  const hasPacks = Boolean(packs?.length);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={hasPacks ? false : 'sm'}>
      <DialogTitle sx={{ fontFamily: 'BrightstarSans-Bold', color: 'var(--reach-blue)' }}>
        {translations.inventoryReview || 'Inventory Review'}
      </DialogTitle>
      <DialogContent
        dividers
        sx={
          hasPacks
            ? {
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                minHeight: 0,
              }
            : undefined
        }
      >
        <Box
          component='form'
          aria-label='Inventory search'
          onSubmit={handleFormSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flexShrink: 0,
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Autocomplete
              sx={{ minWidth: 240 }}
              disabled={productsLoading}
              options={products}
              inputValue={gameQuery}
              onInputChange={(event, value) => setGameQuery(value)}
              filterOptions={options => filterGames(options, gameQuery, gameLabel(selectedGame))}
              value={selectedGame}
              onChange={handleGameChange}
              getOptionLabel={gameLabel}
              getOptionKey={game => String(game.productId)}
              isOptionEqualToValue={(option, value) =>
                String(option.productId) === String(value.productId)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  label='Game'
                  inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                />
              )}
            />
            <TextField
              required
              label='Pack ID'
              value={unitId}
              onChange={event => setUnitId(event.target.value)}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
            <Button
              type='submit'
              variant='contained'
              disabled={!canSubmit}
              className='aurora-search-button'
            >
              Submit
            </Button>
            <Button
              type='button'
              variant='outlined'
              onClick={handleClear}
              sx={{
                fontFamily: 'BrightstarSans-Regular',
                borderColor: '#050A30',
                color: '#050A30',
                minWidth: 140,
                '&:hover': {
                  borderColor: '#050A30',
                  backgroundColor: 'rgba(5, 10, 48, 0.04)',
                },
              }}
            >
              {translations.clear || 'Clear'}
            </Button>
          </Box>
        </Box>

        {productsLoading || searching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0, py: 3 }}>
            <CircularProgress size={28} sx={{ color: 'var(--reach-blue)' }} />
          </Box>
        ) : null}

        {productsError ? (
          <Typography color='error' variant='body2' sx={{ flexShrink: 0 }}>
            {productsError}
          </Typography>
        ) : null}

        {searchError ? (
          <Typography color='error' variant='body2' sx={{ flexShrink: 0 }}>
            {searchError}
          </Typography>
        ) : null}

        {orderNumber ? (
          <Typography
            sx={{
              fontFamily: 'BrightstarSans-Bold',
              color: 'var(--reach-blue)',
              textAlign: 'center',
              width: '100%',
              flexShrink: 0,
              my: 2,
            }}
          >
            Order Number: {orderNumber}
          </Typography>
        ) : null}

        {packs && packs.length === 0 && !searchError ? (
          <Typography variant='body2' color='text.secondary' sx={{ flexShrink: 0 }}>
            No results found.
          </Typography>
        ) : null}

        {packs && packs.length > 0 ? (
          <Box
            sx={{
              flex: '0 1 auto',
              minHeight: 0,
              overflowX: 'scroll',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(5, 10, 48, 0.55) rgba(5, 10, 48, 0.12)',
              '&::-webkit-scrollbar': {
                WebkitAppearance: 'none',
                height: 12,
                width: 12,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(5, 10, 48, 0.55)',
                borderRadius: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(5, 10, 48, 0.12)',
              },
            }}
          >
            <TableContainer sx={{ overflow: 'visible', width: 'max-content', minWidth: '100%' }}>
            <Table size='small' aria-label='inventory packs'>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: 'rgba(5, 10, 48, 0.04)',
                    '& .MuiTableCell-head': {
                      fontFamily: 'BrightstarSans-Bold',
                      color: 'var(--reach-blue)',
                      borderBottomColor: 'rgba(5, 5, 51, 0.1)',
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: '#f4f5f8',
                    },
                  }}
                >
                  <TableCell>Pack ID</TableCell>
                  <TableCell>Retailer ID</TableCell>
                  <TableCell>Location Name</TableCell>
                  <TableCell>Product ID</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Percent Validated</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Confirm Date</TableCell>
                  <TableCell>Activate Date</TableCell>
                  <TableCell>Settle Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Status Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packs.map((pack, index) => (
                  <TableRow
                    key={pack.packId ?? index}
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      '& .MuiTableCell-root': {
                        fontFamily: 'BrightstarSans-Regular',
                        color: 'var(--reach-blue)',
                        borderBottomColor: 'rgba(5, 5, 51, 0.1)',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    <TableCell>{formatCellValue(pack.packId)}</TableCell>
                    <TableCell>{formatCellValue(pack.retailerId)}</TableCell>
                    <TableCell>{formatCellValue(pack.locationName)}</TableCell>
                    <TableCell>{formatCellValue(pack.productId)}</TableCell>
                    <TableCell>{formatCellValue(pack.productName)}</TableCell>
                    <TableCell>
                      {pack.percentValidated == null ? '—' : `${pack.percentValidated}%`}
                    </TableCell>
                    <TableCell>{formatSignOnTimestamp(pack.issueDatetime)}</TableCell>
                    <TableCell>{formatSignOnTimestamp(pack.confirmDatetime)}</TableCell>
                    <TableCell>{formatSignOnTimestamp(pack.activateDatetime)}</TableCell>
                    <TableCell>{formatSignOnTimestamp(pack.settleDatetime)}</TableCell>
                    <TableCell>{formatInventoryReviewStatus(pack.statusId)}</TableCell>
                    <TableCell>{formatSignOnTimestamp(pack.statusChangeDatetime)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translations.close || 'Close'}</Button>
      </DialogActions>
    </Dialog>
  );
}
